import TrackedLink from "@/components/TrackedLink";
import { Button } from "@/components/ui/button";
import {
  Shield,
  ShieldAlert,
  ShieldCheck,
  Fingerprint,
  KeyRound,
  Lock,
  Unlock,
  CheckCircle2,
  XCircle,
  Lightbulb,
  UserCheck,
  Clock,
  Ban,
  Smartphone,
  Fish,
} from "lucide-react";
import { motion } from "framer-motion";
import passkeysHeroBg from "@/assets/passkeys-hero-bg.jpg";
import appleIcon from "@/assets/ms-icons/apple.svg";
import keeperLogo from "@/assets/logo-keeper.png";

const scenarios = [
  {
    icon: KeyRound,
    title: "Wachtwoord123!",
    description:
      "Je medewerkers hergebruiken wachtwoorden. Voor hun mail, voor Teams, soms zelfs voor hun privé-accounts. Eén datalek ergens anders = toegang tot jouw bedrijf.",
  },
  {
    icon: Fish,
    title: "Phishing-mail geopend",
    description:
      "Een medewerker klikt op een link, vult nietsvermoedend zijn wachtwoord in op een nep-inlogpagina. De hacker heeft nu toegang tot je volledige Microsoft 365-omgeving.",
  },
  {
    icon: Clock,
    title: "MFA-moeheid",
    description:
      "Je medewerkers krijgen tientallen keren per dag een MFA-pop-up. Op een druk moment klikken ze op 'Goedkeuren' zonder te kijken. Een hacker rekent hierop.",
  },
  {
    icon: Unlock,
    title: "Gedeelde wachtwoorden",
    description:
      "Het wachtwoord van het gedeelde mailaccount staat op een post-it. Of in een WhatsApp-groep. Of in een Excel-bestand genaamd 'wachtwoorden.xlsx'.",
  },
];

const comparison = {
  without: [
    "Wachtwoorden die gehackt, geraden of gestolen kunnen worden",
    "Phishing-aanvallen waar medewerkers intrappen",
    "MFA-moeheid: blindelings goedkeuren van meldingen",
    "IT-helpdesk overspoeld met wachtwoord-resets",
    "Gedeelde wachtwoorden op post-its en in spreadsheets",
  ],
  with: [
    "Inloggen met vingerafdruk of gezichtsherkenning — niks te stelen",
    "Phishing is onmogelijk: passkeys werken alleen op de echte website",
    "Geen pop-ups meer: één scan en je bent binnen",
    "Geen wachtwoorden = geen wachtwoord-resets",
    "Elke medewerker heeft een unieke, niet-deelbare sleutel",
  ],
};

const voordelen = [
  {
    icon: Shield,
    title: "99,9% minder account-hacks",
    description:
      "Microsoft zegt het zelf: passkeys blokkeren vrijwel alle geautomatiseerde aanvallen. Geen wachtwoord = niks om te hacken.",
  },
  {
    icon: Ban,
    title: "Phishing wordt onmogelijk",
    description:
      "Een passkey werkt alleen op de echte Microsoft-website. Een nep-inlogpagina? Daar doet je passkey simpelweg niks mee.",
  },
  {
    icon: Fingerprint,
    title: "Inloggen met je gezicht of vinger",
    description:
      "Geen wachtwoord onthouden, geen code overtypen. Gewoon je telefoon oppakken, scannen, en je bent binnen.",
  },
  {
    icon: UserCheck,
    title: "Medewerkers zijn er blij mee",
    description:
      "Sneller inloggen, geen wachtwoorden onthouden, geen MFA-gedoe meer. Je medewerkers merken alleen dat het makkelijker wordt.",
  },
  {
    icon: Lightbulb,
    title: "Wij regelen alles",
    description:
      "Wij configureren passkeys voor je hele organisatie. Je medewerkers krijgen een simpele instructie en zijn in 5 minuten klaar.",
  },
  {
    icon: ShieldCheck,
    title: "Werkt op elk apparaat",
    description:
      "iPhone, Android, Windows, Mac — passkeys werken overal. Je medewerkers gebruiken gewoon het apparaat dat ze al hebben.",
  },
];

const PraktijkPasskeys = () => {
  return (
    <>
      {/* Hero */}
      <section className="relative min-h-[420px] overflow-hidden bg-black text-primary-foreground">
        <img loading="lazy" decoding="async"
          src={passkeysHeroBg}
          alt="Medewerker logt in met vingerafdruk"
          className="absolute inset-0 h-full w-full object-cover opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" />
        <div className="container relative z-10 py-20 md:py-32">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-white/10 backdrop-blur-sm"
            >
              <Fingerprint className="h-7 w-7 text-primary" />
            </motion.div>
            <span className="mb-4 inline-block rounded-full bg-primary/20 px-4 py-1.5 text-sm font-semibold text-primary">
              Praktijkvoorbeeld
            </span>
            <h1 className="mb-6 text-4xl font-bold leading-tight md:text-5xl lg:text-6xl">
              Wachtwoorden zijn je{" "}
              <span className="text-primary">grootste zwakte.</span>
            </h1>
            <p className="mb-8 text-lg text-primary-foreground/80 md:text-xl">
              De meeste hacks beginnen met een gestolen wachtwoord. Phishing,
              datalekken, zwakke wachtwoorden — het is allemaal te voorkomen.
              Met passkeys log je in met je vingerafdruk of gezicht. Veiliger,
              sneller, en je medewerkers zijn er blij mee.
            </p>
            <div className="flex flex-wrap gap-x-6 gap-y-3">
              {[
                "Geen wachtwoorden meer",
                "Phishing-bestendig",
                "Wij regelen alles",
                "Elk apparaat",
              ].map((item, i) => (
                <motion.div
                  key={item}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.3 + i * 0.1 }}
                  className="flex items-center gap-2 text-sm text-primary-foreground/90"
                >
                  <CheckCircle2 className="h-4 w-4 text-rinqel-green" />
                  <span>{item}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Herkenning */}
      <section className="py-16 md:py-24">
        <div className="container">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="mb-4 text-3xl font-bold md:text-4xl">
              Herken je dit?
            </h2>
            <p className="mb-12 text-lg text-muted-foreground">
              Dit zijn situaties die bij vrijwel elk bedrijf voorkomen. En ze
              zijn gevaarlijker dan je denkt.
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            {scenarios.map((scenario) => (
              <div
                key={scenario.title}
                className="group rounded-2xl border border-border bg-card p-6 transition-all hover:border-destructive/30 hover:shadow-lg"
              >
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-destructive/10">
                  <scenario.icon className="h-6 w-6 text-destructive" />
                </div>
                <h3 className="mb-2 text-lg font-bold">{scenario.title}</h3>
                <p className="text-muted-foreground">{scenario.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Het probleem */}
      <section className="border-y border-border bg-muted/30 py-16 md:py-24">
        <div className="container">
          <div className="mx-auto max-w-3xl">
            <div className="mb-8 flex h-16 w-16 items-center justify-center rounded-2xl bg-destructive/10">
              <ShieldAlert className="h-8 w-8 text-destructive" />
            </div>
            <h2 className="mb-6 text-3xl font-bold md:text-4xl">
              Waarom zijn wachtwoorden het probleem?
            </h2>
            <div className="space-y-4 text-lg text-muted-foreground">
              <p>
                80% van alle datalekken begint met een gestolen of zwak
                wachtwoord. En het maakt niet uit hoe complex je wachtwoordbeleid
                is — als een medewerker hetzelfde wachtwoord gebruikt voor zijn
                werk-mail en zijn Netflix-account, ben je kwetsbaar.
              </p>
              <p>
                MFA helpt, maar is geen wondermiddel. Medewerkers klikken uit
                gewoonte op 'Goedkeuren'. Hackers weten dit en bombarderen
                accounts met MFA-verzoeken tot iemand per ongeluk accepteert.
                Dat heet <strong>MFA-fatigue</strong> en het werkt
                verbazingwekkend goed.
              </p>
              <p className="font-semibold text-foreground">
                De echte oplossing? Het wachtwoord helemaal afschaffen.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* De oplossing */}
      <section className="bg-rinqel-green/5 py-16 md:py-24">
        <div className="container">
          <div className="mx-auto max-w-3xl">
            <div className="mb-8 flex h-16 w-16 items-center justify-center rounded-2xl bg-rinqel-green/10">
              <ShieldCheck className="h-8 w-8 text-rinqel-green" />
            </div>
            <h2 className="mb-6 text-3xl font-bold md:text-4xl">
              Passkeys: inloggen zonder wachtwoord
            </h2>
            <div className="space-y-4 text-lg text-muted-foreground">
              <p>
                Een passkey is een digitale sleutel die op het apparaat van je
                medewerker staat. In plaats van een wachtwoord in te typen,
                gebruiken ze hun <strong>vingerafdruk</strong>,{" "}
                <strong>gezichtsherkenning</strong> of{" "}
                <strong>pincode van het apparaat</strong>. Dat is het.
              </p>
              <p>
                Er is geen wachtwoord om te stelen, geen code om over te typen,
                en geen pop-up om per ongeluk goed te keuren. De passkey werkt
                bovendien alleen op de echte Microsoft-website — een
                phishing-pagina kan er niks mee.
              </p>
              <p>
                Het resultaat? Je medewerkers loggen{" "}
                <strong>sneller</strong> in én je bedrijf is{" "}
                <strong>véél beter beveiligd</strong>. Win-win.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Apple-sectie */}
      <section className="py-16 md:py-24">
        <div className="container">
          <div className="mx-auto max-w-3xl">
            <div className="mb-8 flex h-16 w-16 items-center justify-center rounded-2xl bg-foreground/5">
              <img loading="lazy" decoding="async" src={appleIcon} alt="Apple logo" className="h-8 w-auto object-contain" />
            </div>
            <h2 className="mb-6 text-3xl font-bold md:text-4xl">
              Extra voordeel voor Apple-gebruikers
            </h2>
            <div className="space-y-4 text-lg text-muted-foreground">
              <p>
                Gebruik je een iPhone, iPad én Mac? Dan wordt het nóg
                makkelijker. Dankzij <strong>iCloud Keychain</strong> worden je
                passkeys automatisch gesynchroniseerd tussen al je
                Apple-apparaten.
              </p>
              <p>
                Dat betekent: je maakt één keer een passkey aan op je iPhone, en
                daarna kun je ook op je MacBook inloggen met{" "}
                <strong>Touch ID</strong> en op je iPad met{" "}
                <strong>Face ID of Touch ID</strong>. Zonder iets extra's in te stellen. Je
                iPhone hoeft niet eens in de buurt te zijn.
              </p>
              <p>
                Telefoon vergeten? Batterij leeg? Maakt niet uit — je passkey
                staat veilig in iCloud en werkt gewoon op je andere
                Apple-apparaten.
              </p>
              <p className="text-sm italic text-muted-foreground/70">
                Let op: iCloud Keychain synchroniseert alleen tussen Apple-apparaten. Gebruik je ook Windows of Android? Lees dan hieronder over Keeper.
              </p>
              <div className="mt-4 rounded-xl border border-rinqel-green/30 bg-rinqel-green/5 p-4 flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-rinqel-green/10">
                  <img loading="lazy" decoding="async" src={appleIcon} alt="Apple" className="h-5 w-auto object-contain" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium">Weten hoe Rinqel je Macs veilig en automatisch kan beheren?</p>
                  <TrackedLink to="/diensten/werkplek/macos" trackLabel="macOS beheer" trackLocation="passkeys-apple-sectie" className="text-sm font-semibold text-rinqel-green hover:underline inline-flex items-center gap-1 mt-0.5">
                    Lees meer over macOS-beheer →
                  </TrackedLink>
                </div>
              </div>
            </div>
            <div className="mt-8 grid gap-4 sm:grid-cols-3">
              {[
                {
                  device: "iPhone",
                  method: "Face ID",
                  description: "Kijk naar je scherm en je bent ingelogd",
                },
                {
                  device: "MacBook",
                  method: "Touch ID",
                  description: "Eén vingerafdruk op de powerknop",
                },
                {
                  device: "iPad",
                  method: "Face ID / Touch ID",
                  description: "Werkt automatisch, ook zonder iPhone",
                },
              ].map((item) => (
                <div
                  key={item.device}
                  className="rounded-xl border border-border bg-card p-5 text-center"
                >
                  <p className="text-sm font-semibold text-primary">
                    {item.device}
                  </p>
                  <p className="mt-1 text-lg font-bold">{item.method}</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Keeper Enterprise sectie */}
      <section className="border-y border-border bg-muted/30 py-16 md:py-24">
        <div className="container">
          <div className="mx-auto max-w-3xl">
            <div className="mb-8 flex h-16 w-16 items-center justify-center rounded-2xl bg-rinqel-blue/10">
              <img loading="lazy" decoding="async" src={keeperLogo} alt="Keeper logo" className="h-8 w-auto object-contain" />
            </div>
            <h2 className="mb-6 text-3xl font-bold md:text-4xl">
              Mix van apparaten? Keeper regelt het.
            </h2>
            <div className="space-y-4 text-lg text-muted-foreground">
              <p>
                Niet iedereen werkt in een volledig Apple-ecosysteem. Veel
                bedrijven hebben een mix van iPhones, Windows-laptops en
                Android-telefoons. Dan werkt iCloud Keychain niet. Maar{" "}
                <strong>Keeper Enterprise</strong> wel.
              </p>
              <p>
                Keeper slaat je passkeys op in een{" "}
                <strong>beveiligde cloud-vault</strong> in plaats van in iCloud
                Keychain. Die vault synchroniseert automatisch naar al je
                apparaten — ongeacht merk of besturingssysteem. iOS, Android,
                Windows, macOS, en alle gangbare browsers.
              </p>
              <p>
                Het resultaat: dezelfde ervaring als Apple-gebruikers. Eén keer
                een passkey aanmaken, en je kunt overal inloggen met{" "}
                <strong>Face ID, Touch ID of andere biometrie</strong>. Je
                telefoon hoeft niet in de buurt te zijn, en je hebt geen
                Apple-ecosysteem nodig.
              </p>
            </div>

            {/* Apple vs Keeper vergelijking */}
            <div className="mt-10 grid gap-6 sm:grid-cols-2">
              <div className="rounded-2xl border border-border bg-card p-6">
                <div className="mb-4 flex items-center gap-3">
                  <img loading="lazy" decoding="async" src={appleIcon} alt="Apple" className="h-6 w-auto object-contain" />
                  <h3 className="text-lg font-bold">Apple iCloud Keychain</h3>
                </div>
                <ul className="space-y-3 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-rinqel-green" />
                    <span>Naadloze sync tussen Apple-apparaten</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-rinqel-green" />
                    <span>Ingebouwd in iOS, iPadOS en macOS</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive/60" />
                    <span>Werkt niet op Windows of Android</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive/60" />
                    <span>Geen centraal beheer voor bedrijven</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive/60" />
                    <span>Bedrijfsgegevens belanden in privé iCloud-backup</span>
                  </li>
                </ul>
              </div>
              <div className="rounded-2xl border border-rinqel-blue/30 bg-card p-6 shadow-lg shadow-rinqel-blue/5">
                <div className="mb-4 flex items-center gap-3">
                  <img loading="lazy" decoding="async" src={keeperLogo} alt="Keeper" className="h-6 w-auto object-contain" />
                  <h3 className="text-lg font-bold text-rinqel-blue">Keeper Enterprise</h3>
                </div>
                <ul className="space-y-3 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-rinqel-green" />
                    <span>Sync naar alle apparaten en browsers</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-rinqel-green" />
                    <span>Werkt op iOS, Android, Windows én macOS</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-rinqel-green" />
                    <span>Centraal beheer en veilig wachtwoorden delen</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-rinqel-green" />
                    <span>Inloggen via Microsoft SSO — geen extra wachtwoord</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-rinqel-green" />
                    <span>Bedrijfsdata blijft gescheiden van privé-accounts</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Bonus: wachtwoorden delen */}
            <div className="mt-10 rounded-2xl border border-primary/20 bg-primary/5 p-6">
              <div className="flex items-start gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10">
                  <Smartphone className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="mb-2 text-lg font-bold">
                    Bonus: veilig wachtwoorden delen met collega's
                  </h3>
                  <p className="text-muted-foreground">
                    Naast passkeys kun je met Keeper ook{" "}
                    <strong>wachtwoorden veilig delen</strong> binnen je team —
                    bijvoorbeeld voor gedeelde accounts, social media of
                    leveranciersportalen. Geen post-its, geen WhatsApp-berichten,
                    geen gedeelde spreadsheets meer. Natuurlijk ook via Rinqel te
                    regelen.
                  </p>
                  <Button variant="outline" size="sm" className="mt-4" asChild>
                    <TrackedLink
                      to="/diensten/werkplek/keeper"
                      trackLabel="Meer over Keeper"
                      trackLocation="praktijk_passkeys_keeper"
                    >
                      Meer over Keeper →
                    </TrackedLink>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-border bg-muted/30 py-16 md:py-24">
        <div className="container">
          <h2 className="mb-12 text-center text-3xl font-bold md:text-4xl">
            Het verschil
          </h2>
          <div className="mx-auto grid max-w-5xl gap-8 md:grid-cols-2">
            {/* Zonder */}
            <div className="rounded-2xl border border-destructive/20 bg-card p-8">
              <div className="mb-6 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10">
                  <XCircle className="h-5 w-5 text-destructive" />
                </div>
                <h3 className="text-xl font-bold text-destructive">
                  Met wachtwoorden
                </h3>
              </div>
              <ul className="space-y-4">
                {comparison.without.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-destructive/60" />
                    <span className="text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Met */}
            <div className="rounded-2xl border border-rinqel-green/30 bg-card p-8 shadow-lg shadow-rinqel-green/5">
              <div className="mb-6 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-rinqel-green/10">
                  <CheckCircle2 className="h-5 w-5 text-rinqel-green" />
                </div>
                <h3 className="text-xl font-bold text-rinqel-green">
                  Met passkeys via Rinqel
                </h3>
              </div>
              <ul className="space-y-4">
                {comparison.with.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-rinqel-green" />
                    <span className="text-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Voordelen */}
      <section className="py-16 md:py-24">
        <div className="container">
          <h2 className="mb-12 text-center text-3xl font-bold md:text-4xl">
            Wat levert het je op?
          </h2>
          <div className="mx-auto grid max-w-5xl gap-6 md:grid-cols-2 lg:grid-cols-3">
            {voordelen.map((voordeel) => (
              <div
                key={voordeel.title}
                className="rounded-2xl border border-border bg-card p-6 transition-all hover:border-rinqel-blue/30 hover:shadow-lg"
              >
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-rinqel-blue/10">
                  <voordeel.icon className="h-6 w-6 text-rinqel-blue" />
                </div>
                <h3 className="mb-2 text-lg font-bold">{voordeel.title}</h3>
                <p className="text-sm text-muted-foreground">
                  {voordeel.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Extra uitleg */}
      <section className="py-12 md:py-16">
        <div className="container">
          <div className="mx-auto max-w-3xl space-y-6 text-muted-foreground">
            <div className="space-y-3">
              <h2 className="text-2xl font-bold text-foreground">Wat we precies doen</h2>
              <p>
                We zetten passkeys aan in jullie Microsoft 365-omgeving en koppelen ze aan de
                vingerafdruk of gezichtsscan op de apparaten die je medewerkers al gebruiken.
                Per medewerker registreren we minimaal twee passkeys, zodat een verloren of
                kapot toestel nooit een probleem is.
              </p>
              <p>
                Daarnaast schakelen we wachtwoorden waar mogelijk uit en sluiten we de oude
                inlogmethodes af die nog rondhangen. Voor uitzonderingen (bijvoorbeeld externe
                accountants) zetten we een veilige terugvaloptie klaar.
              </p>
            </div>

            <div className="space-y-3">
              <h2 className="text-2xl font-bold text-foreground">Wat het oplevert</h2>
              <p>
                Inloggen wordt sneller én veiliger: één tik op de telefoon of laptop, klaar.
                Phishingmails verliezen hun macht, want er valt geen wachtwoord meer te stelen.
                En je medewerkers hoeven nooit meer een wachtwoord te onthouden of te resetten.
              </p>
              <p>
                Voor jou als ondernemer betekent het minder securityrisico, minder helpdeskvragen
                over vergeten wachtwoorden en een omgeving die voldoet aan de strengste
                cyberverzekeringseisen.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-border bg-gradient-to-br from-primary/5 via-rinqel-blue/5 to-rinqel-green/5 py-16 md:py-24">
        <div className="container">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="mb-4 text-3xl font-bold md:text-4xl">
              Klaar om wachtwoorden af te schaffen?
            </h2>
            <p className="mb-8 text-lg text-muted-foreground">
              Wij regelen de volledige overstap naar passkeys voor je
              organisatie. Geen technisch gedoe, geen lange trajecten. Gewoon
              veiliger inloggen, vanaf morgen.
            </p>
            <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
              <Button variant="hero" size="lg" asChild>
                <TrackedLink
                  to="/configurator"
                  trackLabel="Bereken je prijs"
                  trackLocation="praktijk_passkeys_cta"
                >
                  Bereken je prijs
                </TrackedLink>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <TrackedLink
                  to="/contact"
                  trackLabel="Neem contact op"
                  trackLocation="praktijk_passkeys_cta"
                >
                  Neem contact op
                </TrackedLink>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default PraktijkPasskeys;
